import { Component, ChangeDetectionStrategy, Input, EventEmitter, Output, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';

import { PreMedia } from '@app/features/pre-media/pre-media.adapter';
import { ArticleModel } from '@modules/article/models/article.model';
import { PressKit } from '@modules/content-api/adapter/press-kit.adapter';
import { MediaModelType } from '@modules/media/models/media.model';
import { IcsModalComponent } from '@modules/shared/modal/ics-modal/ics-modal.component';
import { DialogService } from '@modules/shared/services/dialog.service';
import { LightboxService } from '@modules/shared/services/lightbox.service';
import { WindowService } from '@modules/shared/services/window.service';
import {
  ActionIcons,
  ButtonItem,
  ActionContainerComponent,
} from '@modules/ui/action-container/action-container.component';
import { MetaItemInterface, MetaItemsComponent } from '@modules/ui/meta-items/meta-items.component';

import { ArchivedBannerComponent } from '../article/archived-banner/archived-banner.component';
import { ArticleGalleryComponent } from '../article/article-gallery/article-gallery.component';
import { ArticleStatusBarComponent } from '../article/article-status-bar/article-status-bar.component';
import { ExpandableCopyComponent } from '../article/expandable-copy/expandable-copy.component';
import { MasonryGalleryComponent } from '../article/masonry-gallery/masonry-gallery.component';
import { ArticleCardComponent } from '../card/article-card/article-card.component';
import { ContactPersonCardComponent } from '../card/contact-person-card/contact-person-card.component';
import { DocumentCardComponent } from '../card/document-card/document-card.component';
import { ExpandableCardWrapperComponent } from '../card/expandable-card-wrapper/expandable-card-wrapper.component';
import { AudioComponent } from '../media/audio/audio.component';
import { ImageComponent } from '../media/image/image.component';
import { LocalizedDatePipe } from '../pipes/localized-date.pipe';
import { ParallaxDirective } from '../shared/directives/parallax.directive';
import { ScrollSpyDirective } from '../shared/scroll-spy/scroll-spy.directive';
import { InfoComponent } from '../ui/info/info.component';

import { PressKitCardWrapperService } from './press-kit-card-wrapper.service';

@Component({
  selector: 'mb-press-kit',
  templateUrl: './press-kit.component.html',
  styleUrls: ['./press-kit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ImageComponent,
    ParallaxDirective,
    InfoComponent,
    ScrollSpyDirective,
    MetaItemsComponent,
    ActionContainerComponent,
    ExpandableCopyComponent,
    ExpandableCardWrapperComponent,
    ArticleCardComponent,
    ArticleGalleryComponent,
    MasonryGalleryComponent,
    DocumentCardComponent,
    AudioComponent,
    ContactPersonCardComponent,
    ArticleStatusBarComponent,
    ArchivedBannerComponent,
    LocalizedDatePipe,
    TranslatePipe,
  ],
})
export class PressKitComponent implements OnInit {
  private lightboxService = inject(LightboxService);
  private windowService = inject(WindowService);
  private pressKitCardWrapperService = inject(PressKitCardWrapperService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private dialogService = inject(DialogService);

  @Input() pressKit!: PressKit;
  @Input() defaultActions!: ButtonItem[];
  @Input() sectionActions: ButtonItem[] = [];
  @Input() pressKitActions: ButtonItem[] = [];

  @Input() preMedia?: PreMedia;

  @Output() actionClick = new EventEmitter<{ action: ActionIcons; resource: any }>();

  public showAllPressKitArticles: boolean = false;

  ngOnInit(): void {
    this.showAllPressKitArticles = this.pressKitCardWrapperService.isCardWrapperOpened(this.pressKit.id);
  }

  public openMediaGallery(list: MediaModelType[], index: number): void {
    this.lightboxService.openMediaGallery(list, index, this.getCurrentRelationship(), this.preMedia);
  }

  public scrollToAnchor(metaItem: MetaItemInterface): void {
    this.windowService.scrollToId(metaItem.data);
  }

  public goToPressArticlePage(article: ArticleModel): void {
    this.router.navigate(['article', article.id], { relativeTo: this.activatedRoute });
  }

  public onTogglePressKitArticlesWrapper(showAll: boolean): void {
    this.showAllPressKitArticles = showAll;
    this.pressKitCardWrapperService.setIsCardWrapperOpened(this.pressKit.id, this.showAllPressKitArticles);
  }

  public openIcsModal() {
    const ref = this.dialogService.open({
      component: IcsModalComponent,
      inputs: {
        icsLink: this.preMedia?.ics_link,
      },
      outputs: {
        confirm: () => {
          ref.close();
          // download ics file
        },
        requestCancel: () => {
          ref.close();
        },
      },
    });
  }

  private getCurrentRelationship() {
    return {
      label: 'CONNECTED_PRESS_KIT',
      text: this.pressKit.title,
      link: {
        url: this.preMedia ? `/pre-media/${this.preMedia.id}` : `/press-kit/${this.pressKit.id}`,
      },
    };
  }
}
